import {useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {App as AntdApp, Select, Modal, Input, Alert} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon, PageHeader} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useSaveSchemaMutation, useUpdateSchemaMutation, useGetSavedSchemaQuery} from "~/reduxs/api/schemaApiSlice";
import {SCHEMA_TYPES, SAMPLE_VALUES, buildJsonLd, parseJsonLdToValues} from "../schemas/definitions";
import DynamicSchemaForm from "../components/DynamicSchemaForm";
import JsonLdOutput from "../components/JsonLdOutput";
import style from "../style/SchemaTool.module.scss";

const cn = className.bind(style);

const TYPE_OPTIONS = Object.entries(SCHEMA_TYPES).map(([value, def]) => ({value, label: def.label}));

/** Parse an toàn chuỗi json_ld đã lưu. */
const parseJson = (str) => {
	try { return JSON.parse(str); } catch (e) { return null; }
};

/**
 * Trang Generator: chọn loại schema → điền form động → xem trước JSON-LD trực tiếp
 * → Copy / Lưu lại.
 *
 * Chế độ SỬA: mở từ danh sách đã lưu (navigate với state {editId}) → tải chi tiết,
 * đổ ngược json_ld vào form (parseJsonLdToValues), nút lưu thành "Cập nhật".
 */
function SchemaGenerator() {

	const {notification} = AntdApp.useApp();
	const location = useLocation();
	const navigate = useNavigate();

	const editId = location.state?.editId || null;
	const {data: editing} = useGetSavedSchemaQuery(editId, {skip: !editId});

	const [type, setType] = useState('Article');
	const [values, setValues] = useState({});

	const [saveOpen, setSaveOpen] = useState(false);
	const [label, setLabel] = useState('');

	const [saveSchema, {isLoading: saving}] = useSaveSchemaMutation();
	const [updateSchema, {isLoading: updating}] = useUpdateSchemaMutation();

	// Chế độ sửa: đổ dữ liệu schema đã lưu vào form khi chi tiết tải xong.
	useEffect(() => {
		if (!editId || !editing) return;
		if (Number(editing.id) !== Number(editId)) return; // RTK giữ data của arg cũ khi đổi arg
		if (!SCHEMA_TYPES[editing.type]) return;

		setType(editing.type);
		setValues(parseJsonLdToValues(editing.type, parseJson(editing.json_ld)));
		setLabel(editing.label || '');
	}, [editId, editing]);

	const json = useMemo(() => buildJsonLd(type, values), [type, values]);

	const changeType = (t) => {
		setType(t);
		setValues({});
	};

	const fillSample = () => {
		setValues(SAMPLE_VALUES[type] || {});
	};

	const exitEdit = () => {
		navigate('/schema/generator', {replace: true, state: null});
		setValues({});
		setLabel('');
	};

	const handleSave = async () => {
		try {
			if (editId) {
				await updateSchema({
					id: editId,
					type,
					label: label.trim() || type,
					json_ld: JSON.stringify(json),
				}).unwrap();
				notification.success({message: 'Thành công', description: 'Đã cập nhật schema'});
			} else {
				await saveSchema({
					type,
					label: label.trim() || type,
					json_ld: JSON.stringify(json),
				}).unwrap();
				notification.success({message: 'Thành công', description: 'Đã lưu schema'});
				setLabel('');
			}
			setSaveOpen(false);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Lưu thất bại')});
		}
	};

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-shapes"
				title="Tạo Schema (JSON-LD)"
				subtitle="Chọn loại và điền thông tin để sinh dữ liệu có cấu trúc cho Google"
				actions={
					<Button to="/schema/validator" white
						leftIcon={<FontAwesomeIcon icon="fa-light fa-shield-check" />}>
						Kiểm tra
					</Button>
				}
			/>

			{editId && (
				<Alert
					type="info" showIcon style={{marginBottom: 16}}
					message={<span>Đang sửa schema đã lưu: <strong>{editing?.label || `#${editId}`}</strong></span>}
					action={<Button small white onClick={exitEdit}>Thoát chế độ sửa</Button>}
				/>
			)}

			<div className={cn('gen-grid')}>
				<div className={cn('gen-form')}>
					<div className={cn('type-select')}>
						<label className={cn('field-label')}>Loại schema</label>
						<Select
							value={type}
							onChange={changeType}
							options={TYPE_OPTIONS}
							style={{width: '100%'}}
						/>
						<div style={{marginTop: 8, textAlign: 'right'}}>
							<Button small outline onClick={fillSample}
								leftIcon={<FontAwesomeIcon icon="fa-light fa-fill-drip" />}>
								Điền dữ liệu mẫu
							</Button>
						</div>
					</div>

					<DynamicSchemaForm type={type} values={values} onChange={setValues} />
				</div>

				<div className={cn('gen-preview')}>
					<JsonLdOutput
						json={json}
						actions={
							<Button small primary loading={saving || updating} onClick={() => setSaveOpen(true)}
								leftIcon={<FontAwesomeIcon icon={editId ? 'fa-light fa-pen' : 'fa-light fa-floppy-disk'} />}>
								{editId ? 'Cập nhật' : 'Lưu'}
							</Button>
						}
					/>
				</div>
			</div>

			<Modal
				open={saveOpen}
				title={editId ? 'Cập nhật schema' : 'Lưu schema'}
				okText={editId ? 'Cập nhật' : 'Lưu'}
				cancelText="Hủy"
				confirmLoading={saving || updating}
				onOk={handleSave}
				onCancel={() => setSaveOpen(false)}
			>
				<p>Đặt tên để dễ tìm lại trong danh sách đã lưu:</p>
				<Input
					value={label}
					placeholder={`vd: ${type} trang chủ`}
					onChange={(e) => setLabel(e.target.value)}
					onPressEnter={handleSave}
				/>
			</Modal>
		</div>
	);
}

export default SchemaGenerator;
